import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import { BeatLoader } from 'react-spinners';
import '../../../../../styles/count_graphs.css';
import { getPolygonBorderColor } from './DwellTimeUtils';
import { BiFullscreen, BiExitFullscreen, BiLineChart, BiBarChartAlt2 } from 'react-icons/bi';
import { peopleDwellColor } from '../../../../../config';


const DwellChart = ({
    selectedYear,
    dailyDwellData,
    dailyDwellDataLoading,
    selectedWeekOfMonth,
    selectedMonthName,
    selectedDay,
    selectedDayIndex, 
    setSelectedDay, 
    selectedDayOfWeek,
    dailyAverage,
    dailyIncrease,
    isLineChart,
    toggleLineChart
}) => {
    const [chartData, setChartData] = useState();
    const [isFullscreen, setIsFullscreen] = useState(false);
    const chartRef = useRef(null);

    const smallScreen = window.innerWidth < 786;

    // Assuming that the weekly dwell data is just a list rather than a list of maps. 
    const prepareDailyDwellChart = () => {
        // Handle empty data
        if (!dailyDwellData || dailyDwellData.length === 0) {
            console.warn("No daily dwell data available");
            return { 
                labels: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
                datasets: [] 
            };
        }

        const dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        //seems like this needs to be a set afterall. 
        const polygonLabels = new Map();

        dailyDwellData.forEach(day => {
            day.forEach((polygon) => {
                polygonLabels.set(polygon.polygon_id, polygon.polygon_name);
            });
        });

        const datasets = () => {
            let index = 0;
            const totalPolygons = polygonLabels.size;
            const result = [];

            for (const [polygon_id, polygon_name] of polygonLabels) {

                result.push({
                    label: `${polygon_name}`,
                    data: dailyDwellData.map(day => {
                        const polygon = day.find(p => p.polygon_id === polygon_id);
                        return polygon ? (polygon.average_dwell_time / 60).toFixed(2) : 0;
                    }),
                    backgroundColor: getPolygonBorderColor(polygon_id),
                    borderColor: getPolygonBorderColor(polygon_id),
                    borderWidth: 3,
                    tension: 0.3
                });
                index++;
            }
            return result;
        };

        return {
            labels: dayLabels,
            datasets: datasets()
        };
    };

    const handleDaySectionClick = (chart, event) => {
        // console.log("Day section clicked");
        const nativeEvent = event.native || event;

        const rect = chart.canvas.getBoundingClientRect();
        let x;
        
        if (nativeEvent.clientX !== undefined) {
            x = nativeEvent.clientX - rect.left;
        } else if (event.x !== undefined) {
            x = event.x - rect.left;
        } else {
            const position = chart.getRelativePosition(event, chart);
            x = position.x;
        }

        const chartArea = chart.chartArea;

        if (x >= chartArea.left && x <= chartArea.right) {
            const dayWidth = (chartArea.right - chartArea.left) / 7; // 7 days
            const dayIndex = Math.floor((x - chartArea.left) / dayWidth);

            const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
            
            if (dayIndex >= 0 && dayIndex < 7) {
                // console.log(`Clicked on day: ${dayNames[dayIndex]}, index: ${dayIndex}`);
                if (setSelectedDay) {
                    setSelectedDay(dayIndex, dayNames[dayIndex]);
                }
            }
        }
    };

    useEffect(() => {
        const preparedChart = prepareDailyDwellChart();
        if (preparedChart) {
            setChartData(preparedChart);
        }
    }, [dailyDwellData]);


    Chart.register(...registerables);

    return (
        <div className='daily-bar-chart-container'>
        <div className={`count-bar-chart-wrapper ${isFullscreen ? 'fullscreen-container' : ''}`}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>

            {/* Title */}
            <div className='graph-title' style={{ flex: '1 1 auto', minWidth: '200px' }}>
                Daily Average Dwell Time 
            </div>

            {/* Controls and Percentage */}

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', justifyContent: 'flex-end', gap: '1rem', flex: '1 1 auto', minWidth: '100px'}}>
                <button className='toggle-chart-button' onClick={() => toggleLineChart('day')}>
                    {isLineChart ? (
                    <BiBarChartAlt2 size={24} title='Switch to Bar Chart' color='white' />
                    ) : (
                    <BiLineChart size={24} title='Switch to Line Chart' color='white' />
                    )}
                </button>
                <button
                    className='toggle-chart-button'
                    onClick={() => setIsFullscreen(prev => !prev)}
                    title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                >
                    {isFullscreen ? (
                    <BiExitFullscreen size={24} color="white" />
                    ) : (
                    <BiFullscreen size={24} color="white" />
                    )}
                </button>


                </div>

            </div>

            <div className={dailyDwellDataLoading ? 'dwell-daily-bar-chart-container-loading' : ''}>
                <div className='chart-total-container'>
                    <div className='chart-total-pill'>
                        <div className="chart-total-label" style={{ color: peopleDwellColor }}>  Avg Daily Dwell (min): {
                            dailyDwellData && dailyDwellData.length > 0
                            ? (() => {
                                const flatData = dailyDwellData.flat().filter(item => item?.average_dwell_time != null);
                                const total = flatData.reduce((sum, item) => sum + item.average_dwell_time, 0);
                                const avg = flatData.length > 0 ? (total / flatData.length / 60).toFixed(1) : '0.0';
                                return avg;
                                })()
                            : '0.0'
                        }
                        </div>
                    </div>
                </div>
                    {
                        isLineChart ?
                            <Line
                                ref={chartRef}
                                data={chartData || { labels: [], datasets: [] }} // Fetch prepared data
                                options={{
                                    responsive: true, // Ensure the chart is responsive
                                    maintainAspectRatio: true, // Ensure the chart maintains its aspect ratio
                                    hover: {
                                        mode: 'nearest',
                                        intersect: false,
                                    },
                                    onClick: (event, elements) => {  // ← Add this
                                        // // console.log("Daily chart clicked!");
                                        const chart = chartRef.current;
                                        if (chart) {
                                            handleDaySectionClick(chart, event);
                                        }
                                    },
                                    onHover: function (event, elements) {
                                        const chartElement = this.canvas;
                                        if (elements && elements.length > 0) {
                                            chartElement.style.cursor = 'pointer';
                                        } else {
                                            chartElement.style.cursor = 'default';
                                        }
                                    },
                                    scales: {
                                        x: {
                                            // stacked: false,
                                            ticks: {
                                                color: (context) => {
                                                    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                                                    const currentDayName = dayNames[selectedDayIndex];
                                                    return context.tick.label === currentDayName ? '#32de84' : '#F8F8F8';
                                                },
                                                font: {
                                                    weight: (context) => {
                                                        const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                                                        const currentDayName = dayNames[selectedDayIndex];
                                                        return context.tick.label === currentDayName ? 'bold' : 'normal';
                                                    },
                                                    size: (context) => {
                                                        const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                                                        const currentDayName = dayNames[selectedDayIndex];
                                                        return context.tick.label === currentDayName ? 16 : 14;
                                                    }
                                                },
                                            },
                                            title: {
                                                display: true,
                                                text: 'Days of the week',
                                                font: {
                                                    size: 14,
                                                    weight: "normal"
                                                },
                                                color: "#ffffffff"

                                            },
                                            grid: {
                                                display: true,
                                                drawBorder: true,
                                                drawOnChartArea: false,
                                                color: '#aaa'
                                            }
                                        },
                                        y: {
                                            ticks: {
                                                color: '#ffffffff',
                                            },
                                            // stacked: false,
                                            title: {
                                                display: true,
                                                text: 'Minutes',
                                                font: {
                                                    size: 14,
                                                    weight: "normal"
                                                },
                                                color: "#ffffffff"

                                            },
                                        },
                                    },
                                    plugins: {
                                        tooltip: {
                                            mode: 'nearest', // Show tooltip for all datasets at the same index
                                        },
                                        legend: {
                                            position: 'top', // Position the legend at the top
                                            labels: {
                                                color: 'white',
                                                font: {
                                                    size: smallScreen ? 8 : 14, // Adjust font size of legend labels
                                                },
                                                boxWidth: smallScreen ? 8 : 40,  // Adjust width of legend marker box
                                                boxHeight: smallScreen ? 8 : 10, // Adjust height of legend marker box (optional, defaults to boxWidth)
                                            },
                                        },

                                    },


                                }}
                            />

                            :
                            <Bar
                                ref={chartRef}
                                data={chartData || { labels: [], datasets: [] }} // Fetch prepared data
                                options={{
                                    responsive: true, // Ensure the chart is responsive
                                    maintainAspectRatio: true, // Ensure the chart maintains its aspect ratio
                                    hover: {
                                        mode: 'nearest',
                                        intersect: false,
                                    },
                                    onClick: (event, elements) => {  // ← Add this
                                        // // console.log("Daily chart clicked!");
                                        const chart = chartRef.current;
                                        if (chart) {
                                            handleDaySectionClick(chart, event);
                                        }
                                    },
                                    onHover: function (event, elements) {
                                        const chartElement = this.canvas;
                                        if (elements && elements.length > 0) {
                                            chartElement.style.cursor = 'pointer';
                                        } else {
                                            chartElement.style.cursor = 'default';
                                        }
                                    },
                                    scales: {
                                        x: {
                                            ticks: {
                                                color: (context) => {
                                                    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                                                    const currentDayName = dayNames[selectedDayIndex];
                                                    return context.tick.label === currentDayName ? '#32de84' : '#F8F8F8';
                                                },
                                                font: {
                                                    weight: (context) => {
                                                        const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                                                        const currentDayName = dayNames[selectedDayIndex];
                                                        return context.tick.label === currentDayName ? 'bold' : 'normal';
                                                    },
                                                    size: (context) => {
                                                        const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                                                        const currentDayName = dayNames[selectedDayIndex];
                                                        return context.tick.label === currentDayName ? 16 : 14;
                                                    }
                                                },
                                            },
                                            title: {
                                                display: true,
                                                text: 'Days of the week',
                                                font: {
                                                    size: 14,
                                                    weight: "normal"
                                                },
                                                color: "#ffffffff"

                                            },
                                            grid: {
                                                display: true,
                                                drawBorder: true,
                                                drawOnChartArea: false,
                                                color: '#aaa'
                                            }
                                        },
                                        y: {
                                            ticks: {
                                                color: '#ffffffff',
                                            },
                                            title: {
                                                display: true,
                                                text: 'Minutes',
                                                font: {
                                                    size: 14,
                                                    weight: "normal"
                                                },
                                                color: "#ffffffff"

                                            },
                                        },
                                    },
                                    plugins: {
                                        tooltip: {
                                            mode: 'nearest', // Show tooltip for all datasets at the same index
                                        },
                                        legend: {
                                            position: 'top', // Position the legend at the top
                                            labels: {
                                                color: 'white',
                                                font: {
                                                    size: smallScreen ? 8 : 14, // Adjust font size of legend labels
                                                },
                                                boxWidth: smallScreen ? 8 : 40,  // Adjust width of legend marker box
                                                boxHeight: smallScreen ? 8 : 10, // Adjust height of legend marker box (optional, defaults to boxWidth)
                                            },
                                        },

                                    },


                                }}
                            />


                    }

                    {
                        dailyDwellDataLoading && <div id="dwell-daily-loading-overlay">
                            <BeatLoader color={'white'} loading={dailyDwellDataLoading} size={15} />
                        </div>
                    }

                </div>
            </div>
        </div>
    )



}


// Memoize component to prevent unnecessary re-renders
export default React.memo(DwellChart, (prevProps, nextProps) => {
    return (
        prevProps.selectedYear === nextProps.selectedYear &&
        prevProps.selectedWeek === nextProps.selectedWeek &&
        prevProps.selectedWeekOfMonth === nextProps.selectedWeekOfMonth &&
        prevProps.selectedMonthName === nextProps.selectedMonthName &&
        prevProps.dailyDwellDataLoading === nextProps.dailyDwellDataLoading &&
        prevProps.isDailyLineChart === nextProps.isDailyLineChart &&
        prevProps.dailyAverage === nextProps.dailyAverage &&
        prevProps.dailyIncrease === nextProps.dailyIncrease &&
        prevProps.dailyDwellData === nextProps.dailyDwellData
    );
});
